<template>
  <div class="mention-suggestion">
    <LLLoader v-if="membersFetcher.isFetching && !members.length" class="mention-suggestion__loader" />
    <LLEmptyState v-else-if="!members.length">{{ $tc('common_amount_members', 0) }}</LLEmptyState>
    <div v-else class="mention-suggestion__list">
      <LLOptionsListButton
        v-for="(item, index) in filteredMembers"
        :key="item.id"
        :preselected="index === selectedIndex"
        class="mention-suggestion__item"
        small
        @click="selectItem(item)"
      >
        <div v-if="!item.anonymousId" class="mention-suggestion__item-row">
          <ProspectAvatar :prospect="item" class="mention-suggestion__item-avatar" />
          <div class="mention-suggestion__item-name">
            {{ item.onlyFullNameWithShortLastName }}
          </div>
        </div>
        <div v-else class="mention-suggestion__item-row">
          <div class="mention-suggestion__member-avatar mention-suggestion__item-avatar">
            {{ anonymousReplyLetter }}
          </div>
          <div class="mention-suggestion__item-name">
            {{ anonymousIdLabel({ anonymousId: item.anonymousId }) }}
          </div>
        </div>
      </LLOptionsListButton>
      <LLLoader v-if="membersFetcher.isPaginationFetching" class="mention-suggestion__pagination-loader" />
    </div>
    <InfiniteScrollObserver
      v-if="!membersFetcher.isFetching && !membersFetcher.isPaginationFinished"
      class="mention-suggestion__pagination"
      root-margin="10px"
      :threshold="0"
      @intersect="fetchMembers"
    ></InfiniteScrollObserver>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { Fetcher } from '@/helpers/fetcher'
import LLLoader from '@/components/common/LLLoader.vue'
import LLEmptyState from '@/components/common/ui-components/LLEmptyState.vue'
import InfiniteScrollObserver from '@/components/utils/InfiniteScrollObserver.vue'
import { ProspectModel } from '@/models/prospect'
import LLOptionsListButton from '@/components/common/LLOptionsList/LLOptionsListButton.vue'
import ProspectAvatar from '@/components/modules/prospects/ProspectAvatar.vue'

export default {
  name: 'MentionSuggestion',
  components: {
    ProspectAvatar,
    LLOptionsListButton,
    InfiniteScrollObserver,
    LLEmptyState,
    LLLoader
  },
  props: {
    query: { type: String, default: '' },
    command: { type: Function, required: true },
    editor: { type: Object, required: true },
    range: { type: Object, required: true },
    chatId: { type: [String, Number], required: true }
  },
  data() {
    return {
      membersFetcher: new Fetcher({ perPage: 20 }),
      selectedIndex: 0,
      debouncedFetchMembers: null
    }
  },
  computed: {
    anonymousReplyLetter() {
      return this.$t('forum.response.anonymous_reply_user_name').slice(0, 1)
    },
    filteredMembers() {
      return this.members.filter(
        (m) => m.fullNameWithShortLastName || m.onlyFullNameWithShortLastName || m.anonymousId
      )
    },
    members() {
      return this.membersFetcher.data
    }
  },
  watch: {
    members() {
      this.selectedIndex = 0
    },
    query() {
      this.membersFetcher.reset()
      this.debouncedFetchMembers()
    }
  },
  created() {
    this.debouncedFetchMembers = debounce(this.fetchMembers, 300)
  },
  mounted() {
    this.fetchMembers()
  },
  methods: {
    anonymousIdLabel({ anonymousId }) {
      return this.$t('forum.response.anonymous_reply_user_name_with_id', { id: anonymousId })
    },
    escapeRegExp(s) {
      return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    },
    normalizeQuery(q) {
      const memberLabel = this.$t('forum.response.anonymous_reply_user_name') || 'Member'
      const moderatorLabel = this.$t('forum.response.moderator_reply_user_name') || 'Moderator'
      const pattern = new RegExp(
        '^\\s*@?\\s*(?:' + this.escapeRegExp(memberLabel) + '|' + this.escapeRegExp(moderatorLabel) + ')\\s*',
        'i'
      )
      return (q || '').toString().replace(pattern, '')
    },
    async refetchMembers() {
      this.membersFetcher.reset()
      await this.fetchMembers()
    },
    async fetchMembers() {
      console.log('fetch')
      try {
        const normalized = this.normalizeQuery(this.query)
        await this.membersFetcher.fetch(async ({ cancelToken, page, perPage }) => {
          const { members, totalRecords } = await this.$api.forum.chats.getMembersSuggestions({
            chatId: this.chatId,
            search: normalized,
            page,
            perPage,
            cancelToken
          })
          const mapped = members.map((m) => {
            if (m.anonymousId) {
              return {
                anonymousId: m.anonymousId
              }
            }
            return ProspectModel.parseFromApi({ prospect: m })
          })
          return { data: mapped, totalRecords }
        })
      } catch (e) {
        console.log(e)
      }
    },
    async selectItem(item) {
      await this.$nextTick()
      if (item.anonymousId) {
        this.editor.commands.setMention({
          range: this.range,
          options: {
            isAnonymous: true,
            anonymousId: item.anonymousId
          }
        })
      } else {
        const { id: prospectId, userId, onlyFullNameWithShortLastName } = item
        this.editor.commands.setMention({
          range: this.range,
          options: {
            prospectId,
            userId,
            fullName: onlyFullNameWithShortLastName
          }
        })
      }

      await this.$nextTick()
      this.editor.commands.focus()
      setTimeout(() => this.editor.commands.insertContent(' '), 1)
    },
    up() {
      this.selectedIndex =
        (((this.selectedIndex - 1) % this.filteredMembers.length) + this.filteredMembers.length) %
        this.filteredMembers.length
    },
    down() {
      this.selectedIndex = (this.selectedIndex + 1) % this.filteredMembers.length
    },
    enter() {
      if (this.filteredMembers[this.selectedIndex]) {
        this.selectItem(this.filteredMembers[this.selectedIndex])
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.mention-suggestion {
  @apply bg-white border border-neutral-01-100 shadow-lg rounded max-h-60 overflow-y-auto;
  min-width: 200px;

  &__list {
    @apply flex flex-col;
  }
  &__loader {
    @apply p-4;
  }

  &__item {
    @apply px-3 py-2 text-left w-full hover:bg-neutral-01-50 cursor-pointer;
    max-width: 300px;
    &--selected {
      @apply bg-neutral-01-100;
    }
  }
  &__item-row {
    @apply min-w-0 flex items-center;
  }
  &__item-avatar {
    @apply rounded-full mr-2 flex-shrink-0 w-6 h-6;
  }
  &__item-name {
    @apply min-w-0 truncate;
  }
  &__pagination-loader {
    @apply p-4;
  }
  &__pagination {
    @apply h-1;
  }
  &__member-avatar {
    @apply rounded-full flex-shrink object-cover bg-neutral-01-400 text-caption-01 text-white flex items-center justify-center;
  }
}
</style>
